/**
 * PublicSchedulePage - Öffentliche Kurs-/Ressourcen-Ansicht für iFrame-Einbettung
 * 
 * Zeigt den Kursplan an und ermöglicht Besuchern, Trainer-Details zu sehen.
 * Kann auf externen Websites eingebettet werden.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Calendar, Clock, MapPin, User, Phone, Mail, Globe, 
  ExternalLink, ChevronLeft, ChevronRight, X, Users
} from 'lucide-react';
import axios from 'axios';
import { et, etp, EMBED_LOCALE } from '../i18n/embedLang';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format time
const formatTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString(EMBED_LOCALE, { hour: '2-digit', minute: '2-digit' });
};

// Helper to format date
const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString(EMBED_LOCALE, { weekday: 'long', day: '2-digit', month: 'long' });
};

// Get days of week
const getWeekDays = (startDate) => {
  const days = [];
  const start = new Date(startDate);
  start.setDate(start.getDate() - start.getDay() + 1); // Monday
  
  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    days.push(day);
  }
  return days;
};

const PublicSchedulePage = () => {
  const [bookings, setBookings] = useState([]);
  const [resources, setResources] = useState([]);
  const [studioName, setStudioName] = useState('');
  const isFullyBooked = (b) =>
    b && b.max_participants != null && b.current_participants != null &&
    Number(b.current_participants) >= Number(b.max_participants);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });
  const [viewMode, setViewMode] = useState('week'); // week or list
  
  // Embed mode - hide header/footer when ?embed=true
  const [isEmbedMode, setIsEmbedMode] = useState(false);

  useEffect(() => {
    // Check for embed mode
    const params = new URLSearchParams(window.location.search);
    const embedMode = params.get('embed') === 'true';
    setIsEmbedMode(embedMode);
    fetchSchedule();
    
    // Set transparent background for seamless iframe embedding
    if (embedMode) {
      document.body.style.backgroundColor = 'transparent';
      document.documentElement.style.backgroundColor = 'transparent';
      document.body.style.background = 'transparent';
      document.documentElement.style.background = 'transparent';
      document.documentElement.classList.add('embed-mode');

      // Robuster Resize-Handler (siehe Bug-Report 05/2026, ehemals
      // fehlte hier komplett — Parent-Frame musste fixe min-height
      // setzen). Misst max() aller Höhenquellen, ResizeObserver mit
      // 50 ms Debounce, mehrfacher Initial-Burst für async-Laden.
      const sendHeight = () => {
        try {
          const doc = document.documentElement;
          const body = document.body;
          const height = Math.max(
            doc.scrollHeight,
            doc.offsetHeight,
            body ? body.scrollHeight : 0,
            body ? body.offsetHeight : 0,
            Math.ceil(doc.getBoundingClientRect().height)
          );
          window.parent.postMessage({ type: 'resize', height }, '*');
        } catch (e) { /* sandboxed */ }
      };
      let debTimer;
      const debounced = () => {
        window.clearTimeout(debTimer);
        debTimer = window.setTimeout(sendHeight, 50);
      };
      const ro = new ResizeObserver(debounced);
      ro.observe(document.documentElement);
      if (document.body) ro.observe(document.body);
      window.addEventListener('load', debounced);
      window.addEventListener('resize', debounced);
      const t1 = setTimeout(sendHeight, 100);
      const t2 = setTimeout(sendHeight, 500);
      const t3 = setTimeout(sendHeight, 1500);
      return () => {
        ro.disconnect();
        window.removeEventListener('load', debounced);
        window.removeEventListener('resize', debounced);
        clearTimeout(debTimer);
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
  }, [currentWeekStart]);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const startDate = new Date(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      endDate.setDate(endDate.getDate() + 14); // 2 weeks

      // Iter 318 (Feb 2026): Studio-Filter aus URL durchreichen, damit
      // pro-Studio-Embeds nur Resources/Bookings dieses Standorts zeigen.
      const params = new URLSearchParams(window.location.search);
      const studioParam = (params.get('studio') || '').trim();
      const tenantParam = (params.get('tenant') || '').trim();

      const response = await axios.get(`${API}/resources/public/schedule`, {
        params: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          ...(studioParam ? { studio_id: studioParam } : {}),
          ...(tenantParam ? { tenant: tenantParam } : {}),
        }
      });
      
      setBookings(response.data.bookings || []);
      setResources(response.data.resources || []);
      setStudioName(response.data.studio_name || '');
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingClick = async (booking) => {
    try {
      const bparams = new URLSearchParams(window.location.search);
      const tenantParam = (bparams.get('tenant') || '').trim();
      const response = await axios.get(`${API}/resources/public/booking/${booking.id}`, {
        params: { ...(tenantParam ? { tenant: tenantParam } : {}) }
      });
      setSelectedBooking(response.data.booking);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      // Show basic info if detailed fetch fails
      setSelectedBooking(booking);
      setShowModal(true);
    }
  };

  const weekDays = useMemo(() => getWeekDays(currentWeekStart), [currentWeekStart]);

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  // Group bookings by day
  const bookingsByDay = useMemo(() => {
    const grouped = {};
    weekDays.forEach(day => {
      const dayStr = day.toISOString().split('T')[0];
      grouped[dayStr] = [];
    });
    
    bookings.forEach(booking => {
      const bookingDate = new Date(booking.start_time).toISOString().split('T')[0];
      if (grouped[bookingDate]) {
        grouped[bookingDate].push(booking);
      }
    });
    
    // Sort each day's bookings by start time
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => a.start_time.localeCompare(b.start_time));
    });
    
    return grouped;
  }, [bookings, weekDays]);

  // Color mapping for resources
  const getResourceColor = (resourceId) => {
    const resource = resources.find(r => r.id === resourceId);
    return resource?.color || '#3B82F6';
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center`} style={{ backgroundColor: isEmbedMode ? 'transparent' : '#0a0a0a' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A227] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen text-white ${isEmbedMode ? 'p-2' : 'p-4'}`} style={{ backgroundColor: isEmbedMode ? 'transparent' : '#0a0a0a' }} data-testid="public-schedule">
      {/* Header - hidden in embed mode */}
      {!isEmbedMode && (
        <div className="max-w-6xl mx-auto mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-[#C9A227]">{et('Kursplan')}{studioName ? ` ${studioName}` : ''}</h1>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'week' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('week')}
                className={viewMode === 'week' ? 'bg-[#C9A227] text-black hover:bg-[#D4AF37]' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
              >
                {et('Woche')}
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-[#C9A227] text-black hover:bg-[#D4AF37]' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
              >
                {et('Liste')}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Week Navigation - compact in embed mode */}
      <div className={`max-w-6xl mx-auto ${isEmbedMode ? 'mb-2' : ''}`}>
        {isEmbedMode && (
          <div className="flex gap-2 mb-2 justify-end">
            <Button
              variant={viewMode === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('week')}
              className={`text-xs ${viewMode === 'week' ? 'bg-[#C9A227] text-black hover:bg-[#D4AF37]' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}`}
            >
              {et('Woche')}
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={`text-xs ${viewMode === 'list' ? 'bg-[#C9A227] text-black hover:bg-[#D4AF37]' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}`}
            >
              {et('Liste')}
            </Button>
          </div>
        )}
        <div className={`flex items-center justify-center gap-4 ${isEmbedMode ? 'mb-2' : 'mb-6'}`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateWeek(-1)}
            className="text-gray-300 hover:text-white hover:bg-[#1a1a1a]"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <span className={`font-medium ${isEmbedMode ? 'text-sm' : 'text-lg'}`}>
            {weekDays[0].toLocaleDateString(EMBED_LOCALE, { day: '2-digit', month: 'short' })} - {weekDays[6].toLocaleDateString(EMBED_LOCALE, { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateWeek(1)}
            className="text-gray-300 hover:text-white hover:bg-[#1a1a1a]"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Schedule Grid */}
      {viewMode === 'week' ? (
        <div className="max-w-6xl mx-auto grid grid-cols-7 gap-2">
          {weekDays.map((day, idx) => {
            const dayStr = day.toISOString().split('T')[0];
            const dayBookings = bookingsByDay[dayStr] || [];
            const isToday = new Date().toISOString().split('T')[0] === dayStr;
            
            return (
              <div key={idx} className="min-h-[200px]">
                <div 
                  className={`text-center p-2 rounded-t-lg font-medium ${
                    isToday ? 'bg-[#C9A227] text-black' : 'bg-[#1a1a1a] text-gray-300'
                  }`}
                >
                  <div className="text-xs uppercase">
                    {day.toLocaleDateString(EMBED_LOCALE, { weekday: 'short' })}
                  </div>
                  <div className="text-lg">
                    {day.getDate()}
                  </div>
                </div>
                <div className="bg-[#1a1a1a]/50 rounded-b-lg p-1 space-y-1 min-h-[150px] border border-[#2a2a2a] border-t-0">
                  {dayBookings.length === 0 ? (
                    <p className="text-center text-gray-600 text-xs py-4">{et('Keine Kurse')}</p>
                  ) : (
                    dayBookings.map((booking, bIdx) => (
                      <button
                        key={`${booking.id}-${bIdx}`}
                        onClick={() => handleBookingClick(booking)}
                        className="w-full text-left p-2 rounded transition-all hover:scale-[1.02] hover:shadow-lg"
                        style={{ 
                          backgroundColor: getResourceColor(booking.resource_id) + '20',
                          borderLeft: `3px solid ${getResourceColor(booking.resource_id)}`
                        }}
                      >
                        <p className="font-medium text-xs text-white truncate">{booking.title}</p>
                        <p className="text-xs text-gray-400">
                          {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{booking.resource_name}</p>
                        {isFullyBooked(booking) && (
                          <span className="inline-block mt-1 text-[10px] font-bold text-red-400 bg-red-500/15 border border-red-500/30 rounded px-1.5 py-0.5" data-testid="course-full-badge">{et('AUSGEBUCHT')}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="max-w-4xl mx-auto space-y-4">
          {Object.entries(bookingsByDay)
            .filter(([_, dayBookings]) => dayBookings.length > 0)
            .map(([dayStr, dayBookings]) => (
              <div key={dayStr}>
                <h3 className="text-lg font-semibold text-[#C9A227] mb-2">
                  {formatDate(dayStr + 'T00:00:00')}
                </h3>
                <div className="space-y-2">
                  {dayBookings.map((booking, idx) => (
                    <Card 
                      key={`${booking.id}-${idx}`}
                      className="bg-[#1a1a1a] border-[#2a2a2a] cursor-pointer hover:bg-[#252525] transition-colors"
                      onClick={() => handleBookingClick(booking)}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div 
                          className="w-1 h-12 rounded-full"
                          style={{ backgroundColor: getResourceColor(booking.resource_id) }}
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-white">{booking.title}</h4>
                          <p className="text-sm text-gray-400">
                            {formatTime(booking.start_time)} - {formatTime(booking.end_time)} • {booking.resource_name}
                          </p>
                        </div>
                        {isFullyBooked(booking) && (
                          <Badge className="bg-red-500/15 text-red-400 border border-red-500/30">{et('Ausgebucht')}</Badge>
                        )}
                        <Badge 
                          style={{ 
                            backgroundColor: getResourceColor(booking.resource_id) + '30',
                            color: getResourceColor(booking.resource_id)
                          }}
                        >
                          {et('Details')}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          {Object.values(bookingsByDay).every(arr => arr.length === 0) && (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{et('Keine Kurse in dieser Woche')}</p>
            </div>
          )}
        </div>
      )}

      {/* Course Details Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#C9A227]">
              {selectedBooking?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              {/* Time & Location */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-300">
                  <Clock className="w-4 h-4 text-[#C9A227]" />
                  <span>
                    {formatDate(selectedBooking.start_time)} <br />
                    {formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <MapPin className="w-4 h-4 text-[#C9A227]" />
                  <span>{selectedBooking.resource_name}</span>
                </div>
                {selectedBooking.max_participants && (
                  <div className="flex items-center gap-2 text-gray-300">
                    <Users className="w-4 h-4 text-[#C9A227]" />
                    <span>{etp('Max. {0} Teilnehmer', [selectedBooking.max_participants])}</span>
                  </div>
                )}
                {isFullyBooked(selectedBooking) ? (
                  <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-semibold text-center" data-testid="course-full-detail">
                    {et('⛔ Dieser Kurs ist ausgebucht — keine freien Plätze')}
                  </div>
                ) : (selectedBooking.max_participants && selectedBooking.current_participants != null) ? (
                  <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-semibold text-center" data-testid="course-free-slots">
                    {etp('✅ Noch {0} freie Plätze', [Math.max(0, selectedBooking.max_participants - selectedBooking.current_participants)])}
                  </div>
                ) : null}
              </div>

              {/* Description */}
              {selectedBooking.description && (
                <div className="p-3 rounded-lg border border-[#1a1a1a]">
                  <p className="text-gray-300 text-sm">{selectedBooking.description}</p>
                </div>
              )}

              {/* Trainer Contact */}
              <div className="border-t border-[#2a2a2a] pt-4">
                <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">
                  {et('Trainer / Kontakt')}
                </h4>
                <div className="space-y-2">
                  {selectedBooking.contact_name && (
                    <div className="flex items-center gap-2 text-white">
                      <User className="w-4 h-4 text-[#C9A227]" />
                      <span className="font-medium">{selectedBooking.contact_name}</span>
                    </div>
                  )}
                  {selectedBooking.contact_phone && (
                    <a 
                      href={`tel:${selectedBooking.contact_phone}`}
                      className="flex items-center gap-2 text-gray-300 hover:text-[#C9A227] transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{selectedBooking.contact_phone}</span>
                    </a>
                  )}
                  {selectedBooking.contact_email && (
                    <a 
                      href={`mailto:${selectedBooking.contact_email}`}
                      className="flex items-center gap-2 text-gray-300 hover:text-[#C9A227] transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      <span>{selectedBooking.contact_email}</span>
                    </a>
                  )}
                  {selectedBooking.contact_website && (
                    <a 
                      href={selectedBooking.contact_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-gray-300 hover:text-[#C9A227] transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                      <span>{et('Website besuchen')}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* Registration Info */}
              {(selectedBooking.registration_link || selectedBooking.registration_info) && (
                <div className="border-t border-[#2a2a2a] pt-4">
                  <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">
                    {et('Anmeldung')}
                  </h4>
                  {selectedBooking.registration_info && (
                    <p className="text-gray-300 text-sm mb-3">{selectedBooking.registration_info}</p>
                  )}
                  {selectedBooking.registration_link && (
                    <a
                      href={selectedBooking.registration_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A227] text-black rounded-lg font-medium hover:bg-[#D4AF37] transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {et('Jetzt anmelden')}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicSchedulePage;
